/* istanbul ignore file */
import { getFragmentData } from '@packages/gql/generated/shopping';
import type {
  CriteoProductsQuery,
  PrudsysSearchQuery,
} from '@packages/gql/generated/shopping/graphql';
import { ProductRecommendationFragmentFragmentDoc } from '@packages/gql/generated/shopping/graphql';
import type { GenericTrackingEvent } from '@packages/tracking';
import { useTracking } from '@packages/tracking';
import { useEffect } from 'react';
import type { PageTemplate, RdeType } from '../../types';

type GtmEvent = {
  pageTemplate: PageTemplate;
  akls: Array<string>;
  rdeTypeAbbr: string;
  outputElementNo: number;
};

type TrackingProps = {
  rdeType: RdeType;
  pageTemplate: PageTemplate;
  elementIndex?: number;
  placementBeacons?: CriteoProductsQuery['criteoViewItem']['placementBeacons'];
  recommendations?: PrudsysSearchQuery['prudsysSearch']['recommendations'];
  trackingCondition: boolean;
};

export const useProductsRecoTracking = ({
  rdeType,
  pageTemplate,
  elementIndex,
  recommendations,
  trackingCondition,
  placementBeacons,
}: TrackingProps) => {
  const dispatchGTMEvent = useTracking();

  useEffect(() => {
    if (trackingCondition && elementIndex !== undefined && recommendations !== undefined) {
      dispatchGTMEvent<GenericTrackingEvent<GtmEvent>>({
        event: 'ga_event',
        eventCategory: 'elementImpression',
        eventLabel: 'productRecoImpression',
        eventValue: {
          rdeTypeAbbr: rdeType,
          outputElementNo: (elementIndex ?? 0) + 1,
          pageTemplate,
          akls: recommendations
            .filter((r) => r.product !== null)
            .map((r) => getFragmentData(ProductRecommendationFragmentFragmentDoc, r).product!.akl),
        },
      });

      // execute seen for criteo products (see JPWP-12403)
      if (rdeType === 'criteoViewItem') {
        recommendations.forEach((recommendation) => {
          const r = getFragmentData(ProductRecommendationFragmentFragmentDoc, recommendation);
          const { trackingUrls } = r.tracking ?? {};

          if (trackingUrls && trackingUrls.length) {
            const { OnLoadBeacon, OnViewBeacon } = trackingUrls.reduce(
              (acc, item) => ({ ...acc, [item.purpose]: item.url }),
              {} as any,
            );

            // TODO: catch errors should be logged somewhere

            if (OnLoadBeacon) {
              fetch(OnLoadBeacon)
                .then((response) => {
                  if (response.status >= 200 && response.status <= 299 && OnViewBeacon) {
                    fetch(OnViewBeacon).catch(() => {});
                  }
                })
                .catch(() => {});
            }
          }
        });

        if (placementBeacons && placementBeacons.length) {
          const { OnLoadBeacon, OnViewBeacon } = placementBeacons.reduce(
            (acc, item) => ({ ...acc, [item.purpose]: item.url }),
            {} as any,
          );

          if (OnLoadBeacon) {
            fetch(OnLoadBeacon)
              .then((response) => {
                if (response.status >= 200 && response.status <= 299 && OnViewBeacon) {
                  fetch(OnViewBeacon).catch(() => {});
                }
              })
              .catch(() => {});
          }
        }
      }
    }
  }, [
    trackingCondition,
    elementIndex,
    recommendations,
    dispatchGTMEvent,
    rdeType,
    pageTemplate,
    placementBeacons,
  ]);
};
